import { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Space, Input, Select, DatePicker, Card, Statistic, Row, Col, Modal, Form, Alert, message, Typography, Drawer } from 'antd';
import { CheckOutlined, CloseOutlined, FlagOutlined, SearchOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, BankOutlined, DollarOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { PageHeader } from '../components/PageHeader';
import TreasuryOperationsView from '../components/TreasuryOperationsView';
import TransferDetailDrawer from '../components/TransferDetailDrawer';
import WithdrawalRequestDetailDrawer from '../components/WithdrawalRequestDetailDrawer';
import { adminWithdrawalEndpoints, type AdminWithdrawal, type AdminWithdrawalFilters } from '../api/adminWithdrawalEndpoints';
import { formatCurrency } from '../utils/formatters';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Text } = Typography;

export function AdminWithdrawals() {
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawal[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [summary, setSummary] = useState({
    pending_total_cents: 0,
    approved_total_cents: 0,
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [searchText, setSearchText] = useState('');

  // Modals
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [markDoneModalOpen, setMarkDoneModalOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<AdminWithdrawal | null>(null);

  // S-H1: Drawer stack state
  type DrawerStackItem =
    | { type: 'treasury' }
    | { type: 'transfer'; id: string }
    | { type: 'withdrawal'; id: string };
  
  const [drawerStack, setDrawerStack] = useState<DrawerStackItem[]>([]);

  // S-H2: Treasury operations filter state (preserved in URL)
  const [treasuryWindow, setTreasuryWindow] = useState<number | undefined>(undefined);
  const [treasuryStatus, setTreasuryStatus] = useState<string | undefined>(undefined);
  const [treasuryType, setTreasuryType] = useState<string | undefined>(undefined);
  const [treasuryPage, setTreasuryPage] = useState<number>(1);

  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [markDoneForm] = Form.useForm();

  // S-H2: Sync drawer stack to URL
  const syncDrawerStackToUrl = useCallback((stack: DrawerStackItem[]) => {
    const params = new URLSearchParams(window.location.search);
    
    // Clear existing drawer params
    params.delete('treasury_ops');
    params.delete('transfer_id');
    params.delete('withdrawal_id');
    params.delete('window');
    params.delete('status');
    params.delete('type');
    params.delete('page');
    
    // Add params based on stack
    const hasTreasury = stack.some(d => d.type === 'treasury');
    if (hasTreasury) {
      params.set('treasury_ops', '1');
      if (treasuryWindow !== undefined) params.set('window', String(treasuryWindow));
      if (treasuryStatus) params.set('status', treasuryStatus);
      if (treasuryType) params.set('type', treasuryType);
      if (treasuryPage > 1) params.set('page', String(treasuryPage));
    }
    
    // Add sub-drawer IDs
    const transfer = stack.find(d => d.type === 'transfer');
    if (transfer) params.set('transfer_id', transfer.id);
    
    const withdrawal = stack.find(d => d.type === 'withdrawal');
    if (withdrawal) params.set('withdrawal_id', withdrawal.id);
    
    const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
  }, [treasuryWindow, treasuryStatus, treasuryType, treasuryPage]);

  // S-H2: Read drawer stack from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasTreasuryOps = params.get('treasury_ops') === '1';
    const transferId = params.get('transfer_id');
    const withdrawalId = params.get('withdrawal_id');
    const window_param = params.get('window');
    const status = params.get('status');
    const type = params.get('type');
    const page = params.get('page');
    
    // Restore treasury filter state
    if (window_param) setTreasuryWindow(Number(window_param));
    if (status) setTreasuryStatus(status);
    if (type) setTreasuryType(type);
    if (page) setTreasuryPage(Number(page));
    
    // Build drawer stack from URL params
    if (hasTreasuryOps || transferId || withdrawalId) {
      const stack: DrawerStackItem[] = [];
      stack.push({ type: 'treasury' });
      
      if (transferId) {
        stack.push({ type: 'transfer', id: transferId });
      }
      if (withdrawalId) {
        stack.push({ type: 'withdrawal', id: withdrawalId });
      }
      
      setDrawerStack(stack);
    }
  }, []);

  // S-H2: Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const hasTreasuryOps = params.get('treasury_ops') === '1';
      const transferId = params.get('transfer_id');
      const withdrawalId = params.get('withdrawal_id');
      
      // Rebuild stack from URL
      if (!hasTreasuryOps && !transferId && !withdrawalId) {
        setDrawerStack([]);
      } else {
        const stack: DrawerStackItem[] = [];
        if (hasTreasuryOps || transferId || withdrawalId) {
          stack.push({ type: 'treasury' });
        }
        if (transferId) {
          stack.push({ type: 'transfer', id: transferId });
        }
        if (withdrawalId) {
          stack.push({ type: 'withdrawal', id: withdrawalId });
        }
        setDrawerStack(stack);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // S-H2: Sync URL when drawer stack changes
  useEffect(() => {
    syncDrawerStackToUrl(drawerStack);
  }, [drawerStack, syncDrawerStackToUrl]);

  // S-H1: Drawer stack helpers
  const openTreasuryDrawer = () => {
    setDrawerStack([{ type: 'treasury' }]);
  };

  const openTransferDrawer = (transferId: string) => {
    setDrawerStack(prev => [...prev, { type: 'transfer', id: transferId }]);
  };

  const openWithdrawalDrawer = (withdrawalId: string) => {
    setDrawerStack(prev => [...prev, { type: 'withdrawal', id: withdrawalId }]);
  };

  const closeTopDrawer = () => {
    setDrawerStack(prev => prev.slice(0, -1));
  };

  const closeAllDrawers = () => {
    setDrawerStack([]);
  };

  // Get current drawer states
  const isTreasuryOpen = drawerStack.some(d => d.type === 'treasury');
  const currentTransfer = drawerStack.find(d => d.type === 'transfer');
  const currentWithdrawal = drawerStack.find(d => d.type === 'withdrawal');

  useEffect(() => {
    fetchWithdrawals();
  }, [pagination.current, pagination.pageSize, statusFilter, dateRange]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const filters: AdminWithdrawalFilters = {
        status: statusFilter,
        page: pagination.current,
        per: pagination.pageSize,
        search: searchText || undefined,
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.from = dateRange[0].format('YYYY-MM-DD');
        filters.to = dateRange[1].format('YYYY-MM-DD');
      }

      const data = await adminWithdrawalEndpoints.getQueue(filters);
      
      setWithdrawals(data.withdrawals);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total_count,
      }));
      setSummary(data.summary);
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
      message.error('Failed to load withdrawal queue');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchWithdrawals();
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total,
    });
  };

  const openApproveModal = (withdrawal: AdminWithdrawal) => {
    setSelectedWithdrawal(withdrawal);
    approveForm.resetFields();
    setApproveModalOpen(true);
  };

  const openRejectModal = (withdrawal: AdminWithdrawal) => {
    setSelectedWithdrawal(withdrawal);
    rejectForm.resetFields();
    setRejectModalOpen(true);
  };

  const openMarkDoneModal = (withdrawal: AdminWithdrawal) => {
    setSelectedWithdrawal(withdrawal);
    markDoneForm.resetFields();
    setMarkDoneModalOpen(true);
  };

  const handleApproveAndPayout = async (values: { admin_note?: string }) => {
    if (!selectedWithdrawal) return;

    try {
      // Use combined atomic endpoint - if payout fails, approval is rolled back
      await adminWithdrawalEndpoints.approveAndPayout(selectedWithdrawal.id, {
        admin_note: values.admin_note,
      });
      
      message.success('Withdrawal approved and payout initiated successfully');
      setApproveModalOpen(false);
      fetchWithdrawals();
    } catch (error: any) {
      console.error('Failed to approve and payout withdrawal:', error);
      message.error(error.response?.data?.error || 'Failed to approve and initiate payout');
    }
  };

  const handleApprove = async (values: { admin_note?: string }) => {
    if (!selectedWithdrawal) return;

    try {
      await adminWithdrawalEndpoints.approve(selectedWithdrawal.id, {
        admin_note: values.admin_note,
      });
      message.success('Withdrawal approved successfully (manual mode)');
      setApproveModalOpen(false);
      fetchWithdrawals();
    } catch (error: any) {
      console.error('Failed to approve withdrawal:', error);
      message.error(error.response?.data?.error || 'Failed to approve withdrawal');
    }
  };

  const handleReject = async (values: { admin_note: string }) => {
    if (!selectedWithdrawal) return;

    try {
      await adminWithdrawalEndpoints.reject(selectedWithdrawal.id, {
        admin_note: values.admin_note,
      });
      message.success('Withdrawal rejected');
      setRejectModalOpen(false);
      fetchWithdrawals();
    } catch (error: any) {
      console.error('Failed to reject withdrawal:', error);
      message.error(error.response?.data?.error || 'Failed to reject withdrawal');
    }
  };

  const handleMarkDone = async (values: { admin_note?: string }) => {
    if (!selectedWithdrawal) return;

    try {
      await adminWithdrawalEndpoints.markDone(selectedWithdrawal.id, {
        admin_note: values.admin_note,
      });
      message.success('Withdrawal marked as completed');
      setMarkDoneModalOpen(false);
      fetchWithdrawals();
    } catch (error: any) {
      console.error('Failed to mark withdrawal as done:', error);
      message.error(error.response?.data?.error || 'Failed to mark withdrawal as done');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      pending: { color: 'orange', label: 'Pending' },
      approved: { color: 'blue', label: 'Approved' },
      rejected: { color: 'red', label: 'Rejected' },
      done: { color: 'green', label: 'Done' },
      canceled: { color: 'default', label: 'Canceled' },
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const columns: ColumnsType<AdminWithdrawal> = [
    {
      title: 'Requested',
      dataIndex: 'requested_at',
      key: 'requested_at',
      width: 180,
      render: (date: string) => dayjs(date).format('MMM D, YYYY h:mm A'),
      sorter: (a, b) => dayjs(a.requested_at).unix() - dayjs(b.requested_at).unix(),
    },
    {
      title: 'Type',
      dataIndex: 'request_type',
      key: 'request_type',
      width: 110,
      render: (type: string) => {
        if (type === 'loc_draw') {
          return <Tag color="blue">LOC Draw</Tag>;
        }
        return <Tag color="green">Withdrawal</Tag>;
      },
      filters: [
        { text: 'LOC Draw', value: 'loc_draw' },
        { text: 'Withdrawal', value: 'withdrawal' },
      ],
      onFilter: (value, record) => record.request_type === value,
    },
    {
      title: 'User',
      key: 'user',
      width: 220,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.user_name || 'N/A'}</div>
          <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{record.user_email}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount_cents',
      key: 'amount_cents',
      width: 120,
      render: (cents: number) => formatCurrency(cents),
      sorter: (a, b) => a.amount_cents - b.amount_cents,
    },
    {
      title: 'Current Balance',
      key: 'current_balance',
      width: 140,
      render: (_, record) => {
        // For LOC draws, show available credit; for withdrawals, show withdrawable balance
        if (record.request_type === 'loc_draw') {
          return formatCurrency(record.available_credit_cents || 0);
        }
        return formatCurrency(record.user_withdrawable_cents || 0);
      },
    },
    {
      title: 'Plaid',
      dataIndex: 'has_plaid_linked',
      key: 'has_plaid_linked',
      width: 100,
      align: 'center',
      render: (hasPlaid: boolean) => (
        hasPlaid ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
        )
      ),
      filters: [
        { text: 'Plaid Connected', value: true },
        { text: 'No Plaid', value: false },
      ],
      onFilter: (value, record) => record.has_plaid_linked === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'User Note',
      dataIndex: 'user_note',
      key: 'user_note',
      width: 200,
      ellipsis: true,
      render: (note: string) => note || '-',
    },
    {
      title: 'Admin Note',
      dataIndex: 'admin_note',
      key: 'admin_note',
      width: 200,
      ellipsis: true,
      render: (note: string) => note || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        if (record.status === 'pending') {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => openApproveModal(record)}
              >
                Approve
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => openRejectModal(record)}
              >
                Reject
              </Button>
            </Space>
          );
        }
        
        if (record.status === 'approved') {
          return (
            <Button
              type="default"
              size="small"
              icon={<FlagOutlined />}
              onClick={() => openMarkDoneModal(record)}
            >
              Mark Done
            </Button>
          );
        }

        return <span style={{ color: '#8c8c8c' }}>No actions</span>;
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Withdrawal Queue"
        description="Review and process user withdrawal requests"
        extra={
          <Button
            type="default"
            icon={<DollarOutlined />}
            onClick={openTreasuryDrawer}
          >
            View Treasury Operations
          </Button>
        }
      />

      {/* Summary Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Pending Requests"
              value={withdrawals.filter(w => w.status === 'pending').length}
              suffix={`/ ${pagination.total}`}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Pending Amount"
              value={formatCurrency(summary.pending_total_cents)}
              styles={{ content: { color: '#fa8c16' } }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Approved Amount"
              value={formatCurrency(summary.approved_total_cents)}
              styles={{ content: { color: '#1890ff' } }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%' }}>
          <Select
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'All Statuses', value: '' },
              { label: 'Pending', value: 'pending' },
              { label: 'Approved', value: 'approved' },
              { label: 'Rejected', value: 'rejected' },
              { label: 'Done', value: 'done' },
              { label: 'Canceled', value: 'canceled' },
            ]}
          />
          
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="MMM D, YYYY"
            style={{ width: 280 }}
          />

          <Input
            placeholder="Search by user name or email"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 280 }}
          />

          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
          >
            Search
          </Button>

          <Button
            icon={<ReloadOutlined />}
            onClick={fetchWithdrawals}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={withdrawals}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} requests`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* Approve Modal */}
      <Modal
        title="Approve Withdrawal Request"
        open={approveModalOpen}
        onCancel={() => setApproveModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={600}
      >
        {selectedWithdrawal && (
          <>
            {/* Bank Status Banner */}
            <Alert
              message={
                <Space>
                  <BankOutlined />
                  <Text strong>
                    Bank: {selectedWithdrawal.has_plaid_linked ? (
                      <Text type="success">Connected</Text>
                    ) : (
                      <Text type="danger">Not Connected</Text>
                    )}
                  </Text>
                </Space>
              }
              description={
                selectedWithdrawal.has_plaid_linked && selectedWithdrawal.bank_account_snapshot ? (
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    {selectedWithdrawal.bank_account_snapshot.institution_name && (
                      <div>{selectedWithdrawal.bank_account_snapshot.institution_name}</div>
                    )}
                    {selectedWithdrawal.bank_account_snapshot.account_name && (
                      <div>{selectedWithdrawal.bank_account_snapshot.account_name} 
                        {selectedWithdrawal.bank_account_snapshot.mask && ` ****${selectedWithdrawal.bank_account_snapshot.mask}`}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    User must connect bank account or use manual payout
                  </div>
                )
              }
              type={selectedWithdrawal.has_plaid_linked ? "success" : "warning"}
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Alert
              title="Balance Verification"
              description={
                <div>
                  <p><strong>Requested Amount:</strong> {formatCurrency(selectedWithdrawal.amount_cents)}</p>
                  {selectedWithdrawal.request_type === 'loc_draw' ? (
                    <>
                      <p><strong>User's Current Balance:</strong> {formatCurrency(selectedWithdrawal.available_credit_cents || 0)}</p>
                      <p><strong>Credit Limit:</strong> {formatCurrency(selectedWithdrawal.credit_limit_cents || 0)}</p>
                      {(selectedWithdrawal.available_credit_cents || 0) < selectedWithdrawal.amount_cents && (
                        <Alert
                          type="error"
                          title="Insufficient Balance"
                          description="User's available credit is less than the requested amount. Approval will fail."
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <p><strong>User's Current Balance:</strong> {formatCurrency(selectedWithdrawal.user_withdrawable_cents)}</p>
                      <p><strong>Balance at Request:</strong> {formatCurrency(selectedWithdrawal.withdrawable_cents_at_request)}</p>
                      {selectedWithdrawal.user_withdrawable_cents < selectedWithdrawal.amount_cents && (
                        <Alert
                          type="error"
                          title="Insufficient Balance"
                          description="User's current balance is less than the requested amount. Approval will fail."
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </>
                  )}
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {selectedWithdrawal.user_note && (
              <Alert
                title="User Note"
                description={selectedWithdrawal.user_note}
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={approveForm}
              layout="vertical"
            >
              <Form.Item
                label="Admin Note (Optional)"
                name="admin_note"
              >
                <TextArea
                  rows={3}
                  placeholder="Add a note for the user (optional)"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }} size="middle">
                  <Button onClick={() => setApproveModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="default"
                    icon={<CheckOutlined />}
                    disabled={
                      selectedWithdrawal.request_type === 'loc_draw'
                        ? (selectedWithdrawal.available_credit_cents || 0) < selectedWithdrawal.amount_cents
                        : selectedWithdrawal.user_withdrawable_cents < selectedWithdrawal.amount_cents
                    }
                    onClick={() => {
                      approveForm.validateFields().then(values => {
                        handleApprove(values);
                      });
                    }}
                  >
                    Approve & Manual
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    disabled={
                      !selectedWithdrawal.has_plaid_linked ||
                      (selectedWithdrawal.request_type === 'loc_draw'
                        ? (selectedWithdrawal.available_credit_cents || 0) < selectedWithdrawal.amount_cents
                        : selectedWithdrawal.user_withdrawable_cents < selectedWithdrawal.amount_cents)
                    }
                    onClick={() => {
                      approveForm.validateFields().then(values => {
                        handleApproveAndPayout(values);
                      });
                    }}
                  >
                    Approve & Payout
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title="Reject Withdrawal Request"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {selectedWithdrawal && (
          <>
            <Alert
              title="Request Details"
              description={
                <div>
                  <p><strong>User:</strong> {selectedWithdrawal.user_name} ({selectedWithdrawal.user_email})</p>
                  <p><strong>Amount:</strong> {formatCurrency(selectedWithdrawal.amount_cents)}</p>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {selectedWithdrawal.user_note && (
              <Alert
                title="User Note"
                description={selectedWithdrawal.user_note}
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}

            <Form
              form={rejectForm}
              onFinish={handleReject}
              layout="vertical"
            >
              <Form.Item
                label="Rejection Reason"
                name="admin_note"
                rules={[
                  { required: true, message: 'Please provide a reason for rejection' },
                  { min: 10, message: 'Reason must be at least 10 characters' },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Explain why this withdrawal is being rejected (required)"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setRejectModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    danger
                    htmlType="submit"
                    icon={<CloseOutlined />}
                  >
                    Reject
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* Mark Done Modal */}
      <Modal
        title="Mark Withdrawal as Completed"
        open={markDoneModalOpen}
        onCancel={() => setMarkDoneModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        {selectedWithdrawal && (
          <>
            <Alert
              title="Confirmation"
              description={
                <div>
                  <p>Mark this withdrawal as completed after the off-platform payout has been sent.</p>
                  <p><strong>User:</strong> {selectedWithdrawal.user_name} ({selectedWithdrawal.user_email})</p>
                  <p><strong>Amount:</strong> {formatCurrency(selectedWithdrawal.amount_cents)}</p>
                  <p><strong>Approved:</strong> {dayjs(selectedWithdrawal.resolved_at).format('MMM D, YYYY h:mm A')}</p>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form
              form={markDoneForm}
              onFinish={handleMarkDone}
              layout="vertical"
            >
              <Form.Item
                label="Completion Note (Optional)"
                name="admin_note"
              >
                <TextArea
                  rows={3}
                  placeholder="Add any final notes (optional)"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button onClick={() => setMarkDoneModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<FlagOutlined />}
                  >
                    Mark Done
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* S-F2: Operations Drawer */}
      <Drawer
        title="Treasury Operations"
        placement="right"
        width={1200}
        onClose={closeAllDrawers}
        open={isTreasuryOpen}
        zIndex={1000}
      >
        <TreasuryOperationsView
          onTransferClick={openTransferDrawer}
          onWithdrawalClick={openWithdrawalDrawer}
          initialWindow={treasuryWindow}
          initialStatus={treasuryStatus}
          initialType={treasuryType}
          initialPage={treasuryPage}
          onFilterChange={(filters) => {
            if (filters.window !== undefined) setTreasuryWindow(filters.window);
            if (filters.status !== undefined) setTreasuryStatus(filters.status);
            if (filters.type !== undefined) setTreasuryType(filters.type);
            if (filters.page !== undefined) setTreasuryPage(filters.page);
          }}
        />
      </Drawer>

      {/* S-H1: Transfer Detail Sub-Drawer */}
      <TransferDetailDrawer
        transferId={currentTransfer?.id || null}
        open={!!currentTransfer}
        onClose={closeTopDrawer}
        onWithdrawalClick={openWithdrawalDrawer}
        zIndex={1001}
      />

      {/* S-H1: Withdrawal Request Detail Sub-Drawer */}
      <WithdrawalRequestDetailDrawer
        withdrawalId={currentWithdrawal?.id || null}
        open={!!currentWithdrawal}
        onClose={closeTopDrawer}
        onTransferClick={openTransferDrawer}
        zIndex={1002}
      />
    </div>
  );
}
